import {
  PARAM_ALLOW_AIRPORT_CHANGE,
  PARAM_STOPS,
  PARAM_STOPS_DURATION_END,
  PARAM_STOPS_DURATION_START,
} from '@optionway/models';

interface StopsFilterStore {
  stopsValue: number;
  stopDurationValue: { start: number; end: number };
  allowAirportChange: boolean;
}

const STOPS_FILTER_DEFAULTS: StopsFilterStore = {
  stopsValue: 2,
  stopDurationValue: { start: 0, end: 24 },
  allowAirportChange: true,
};

export const useStopsFilterStore = defineStore('filters/stops', {
  state: (): StopsFilterStore => ({
    stopsValue: STOPS_FILTER_DEFAULTS.stopsValue,
    stopDurationValue: {
      start: STOPS_FILTER_DEFAULTS.stopDurationValue.start,
      end: STOPS_FILTER_DEFAULTS.stopDurationValue.end,
    },
    allowAirportChange: STOPS_FILTER_DEFAULTS.allowAirportChange,
  }),
  getters: {
    hasActiveFilters: (state) =>
      state.stopsValue !== STOPS_FILTER_DEFAULTS.stopsValue ||
      state.stopDurationValue.start !== STOPS_FILTER_DEFAULTS.stopDurationValue.start ||
      state.stopDurationValue.end !== STOPS_FILTER_DEFAULTS.stopDurationValue.end ||
      state.allowAirportChange !== STOPS_FILTER_DEFAULTS.allowAirportChange,
    // stopsValue 2 means "any number of stops" — sending 2 would wrongly cap results to 2 stops max
    maxStop(): number | undefined {
      return this.stopsValue < 2 ? this.stopsValue : undefined;
    },
    maxConnectionTime(): number | undefined {
      return this.stopDurationValue.end < 24 ? this.stopDurationValue.end : undefined;
    },
    minConnectionTime(): number | undefined {
      return this.stopDurationValue.start > 0 ? this.stopDurationValue.start : undefined;
    },
    // Zero-width connection-time window: both bounds active (not at their sentinel values) and start >= end.
    isImpossible(): boolean {
      return (
        this.stopDurationValue.start > 0 &&
        this.stopDurationValue.end < 24 &&
        this.stopDurationValue.start >= this.stopDurationValue.end
      );
    },
    // allowAirportChange is the semantic inverse of the API field
    disableMultiAirportConnection(): true | undefined {
      return this.allowAirportChange ? undefined : true;
    },
    filterQueryString(): string {
      if (!this.hasActiveFilters) return '';
      const parameters = new URLSearchParams();
      if (this.maxStop !== undefined) parameters.set(PARAM_STOPS, String(this.stopsValue));
      if (this.minConnectionTime !== undefined)
        parameters.set(PARAM_STOPS_DURATION_START, String(this.stopDurationValue.start));
      if (this.maxConnectionTime !== undefined)
        parameters.set(PARAM_STOPS_DURATION_END, String(this.stopDurationValue.end));
      if (!this.allowAirportChange) parameters.set(PARAM_ALLOW_AIRPORT_CHANGE, 'false');
      return parameters.toString();
    },
  },
  actions: {
    applyFromUrlQuery(url: string) {
      this.$reset();
      const parameters = new URL(url, 'https://dummy.com').searchParams;
      const stops = Number.parseInt(parameters.get(PARAM_STOPS) ?? '', 10);
      if (!Number.isNaN(stops)) this.stopsValue = Math.min(Math.max(stops, 0), 2);
      const allowAirportChange = parameters.get(PARAM_ALLOW_AIRPORT_CHANGE);
      if (allowAirportChange === 'true' || allowAirportChange === 'false')
        this.allowAirportChange = allowAirportChange === 'true';
      const stopDurationBegin = Number.parseInt(parameters.get(PARAM_STOPS_DURATION_START) ?? '', 10);
      if (!Number.isNaN(stopDurationBegin)) this.stopDurationValue.start = Math.min(Math.max(stopDurationBegin, 0), 24);
      const stopDurationEnd = Number.parseInt(parameters.get(PARAM_STOPS_DURATION_END) ?? '', 10);
      if (!Number.isNaN(stopDurationEnd)) this.stopDurationValue.end = Math.min(Math.max(stopDurationEnd, 0), 24);
    },
  },
});
